// src/components/LatestArtists/LatestArtists.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import ArtistCard from "../Esiintyjakortti/Esiintyjakortti.jsx";

/**
 * Buukkerin uusimmat esiintyjät – noutaa /api/bands -listan
 * ja näyttää siitä kolme uusinta ArtistCard-komponentilla.
 *
 * “Uusimman” määrittely:
 *  - ensisijaisesti approved_at (milloin profiili hyväksytty / julkaistu)
 *  - jos ei ole, fallback submitted_for_review_at / created_at
 */
export default function LatestArtists({ onViewProfile }) {
  const navigate = useNavigate();
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pieni apuri: normalisoi /bands-vastaus taulukoksi
  const toArray = (res) => {
    // Tuetaan useita muotoja:
    // - [{...}, {...}]
    // - { items:[...], ... }
    // - { bands:[...], ... }
    // - { data:{ items:[...] } }
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.bands)) return res.bands;
    if (Array.isArray(res?.data?.items)) return res.data.items;
    return [];
  };

  // Normalisoi aikaleima numeroksi lajittelua varten
  const ts = (row) => {
    const raw =
      row?.approved_at ||
      row?.approvedAt ||
      row?.submitted_for_review_at ||
      row?.submittedForReviewAt ||
      row?.created_at ||
      row?.createdAt ||
      0;

    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const fetchLatest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Haetaan hieman reilumpi lista (esim. 50 kpl),
      // joista poimitaan kolme uusinta frontissa.
      const res = await api.get("/bands?limit=50");
      const rows = toArray(res);

      // Varmuuden vuoksi järjestetään itse:
      // isoin aikaleima ensin → uusin kärkeen
      const sorted = [...rows].sort((a, b) => ts(b) - ts(a));

      // Ota vain kolme näkyviin
      setBands(sorted.slice(0, 3));

      // Debugia varten (voit kommentoida pois kun ei tarvita)
      // console.debug("LatestArtists rows:", sorted.map(b => ({
      //   id: b.id,
      //   name: b.name,
      //   approved_at: b.approved_at,
      //   created_at: b.created_at,
      // })));
    } catch (err) {
      console.error("LatestArtists: haku epäonnistui", err);
      setError(
        "Esiintyjien lataus epäonnistui, yritä hetken kuluttua uudelleen."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Navigointi: kortti + "VARAA HETI!" -nappi → esiintyjän profiili
  const handleViewProfile = useCallback(
    (slug) => {
      if (!slug) return;
      if (typeof onViewProfile === "function") {
        onViewProfile(slug);
        return;
      }
      navigate(`/esiintyja/${encodeURIComponent(slug)}/`);
    },
    [navigate, onViewProfile]
  );

  // Noudetaan heti mountissa
  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  return (
    <section className="max-w-6xl mx-auto px-4 pt-16 pb-10">
      <h2 className="font-customFont2 text-3xl text-black/80 dark:text-white/80 sm:text-4xl lg:text-5xl text-center my-4">
        Buukkerin uusimmat esiintyjät
      </h2>

      {/* Virheilmoitus */}
      {error && (
        <div className="mb-6 rounded-lg bg-red-900/70 text-red-100 border border-red-500 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Lataustila */}
      {loading && !error && (
        <p className="text-sm text-white/70">Ladataan esiintyjiä…</p>
      )}

      {/* Tyhjä tila */}
      {!loading && !error && bands.length === 0 && (
        <p className="text-sm text-white/70">
          Ei vielä yhtään esiintyjää tälle haulle.
        </p>
      )}

      {/* Kortit */}
      {!loading && !error && bands.length > 0 && (
        <div className="grid grid-cols-2 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-1 xsm:grid-cols-1 sm:grid-cols-2 mdd:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 w-full mt-4">
          {bands.map((band, idx) => (
            <ArtistCard
              key={band.id || band.slug || idx}
              band={band}
              onViewProfile={handleViewProfile}
              // Kaikki top-3 ovat “uusia esiintyjiä”
              highlight
              badgeText="Uusi esiintyjä"
              enableVideo={false}
            />
          ))}
        </div>
      )}
    </section>
  );
}
